<script setup>
import Header from "@/components/Header.vue";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import heroImage from "@/assets/img/Evolve/img_8.jpg";
import { loadSlim } from "tsparticles-slim";
import { getEventoActivo } from "@/utils/api_evento";
import { ref, onMounted } from "vue";

const eventoInformacion = ref(null);

const particleConfig = {
  background: { color: "transparent" },
  particles: {
    number: { value: 40 },
    color: { value: "#cc0000" },
    shape: { type: "circle" },
    opacity: { value: 0.25 },
    size: { value: 3 },
    move: { enable: true, speed: 1.2, direction: "none", outModes: "out" },
  },
};

const particlesInit = async (engine) => {
  await loadSlim(engine);
};

// Fecha tipo "sábado 27 de septiembre de 2025"
const fechaFormateada = (fechaStr) => {
  if (!fechaStr) return "";
  const [year, month, day] = fechaStr.split("-").map(Number);
  const fechaLocal = new Date(year, month - 1, day);
  return format(fechaLocal, "EEEE d 'de' MMMM 'de' yyyy", { locale: es });
};

// Hora tipo "07:00 PM"
const horaFormateada = (horaStr) => {
  if (!horaStr) return "";
  const [h, m] = horaStr.split(":");
  const hora = new Date();
  hora.setHours(Number(h));
  hora.setMinutes(Number(m));
  return format(hora, "hh:mm a", { locale: es });
};

onMounted(async () => {
  eventoInformacion.value = await getEventoActivo();
});
</script>

<template>
  <section id="informacion" class="info-wrapper">
    <div class="hero-bg-image" :style="{ backgroundImage: `url(${heroImage})` }"></div>
    <div class="hero-gradient"></div>
    <Particles id="infoParticles" :init="particlesInit" :options="particleConfig" />
    <div class="smoke-layer"></div>

    <div class="info-content">
      <Header />

      <div class="info-section" data-aos="fade-up">
        <div class="info-title">
          <h2>Información del Evento</h2>
          <p>Detalles generales del evento.</p>
        </div>

        <div class="info-grid" data-aos="fade-up" data-aos-delay="100">
          <!-- CARD 1: Fecha evento + Fecha pesaje -->
          <article class="info-card">
            <div class="info-card__icon">
              <i class="bi bi-calendar-event"></i>
            </div>
            <h3 class="info-card__title">Fechas</h3>

            <div class="fecha-bloque">
              <p class="info-card__label">Evento</p>
              <p class="info-card__value">
                {{ fechaFormateada(eventoInformacion?.fecha_evento) }}
              </p>
            </div>

            <div class="fecha-divider" v-if="eventoInformacion?.fecha_pesaje"></div>

            <div class="fecha-bloque" v-if="eventoInformacion?.fecha_pesaje">
              <p class="info-card__label">Pesaje</p>
              <p class="info-card__value">
                {{ fechaFormateada(eventoInformacion?.fecha_pesaje) }}
                <span v-if="eventoInformacion?.hora_pesaje" class="pesaje-hora">
                  · {{ horaFormateada(eventoInformacion?.hora_pesaje) }}
                </span>
              </p>
            </div>
          </article>

          <!-- CARD 2: Ubicación -->
          <article class="info-card">
            <div class="info-card__icon">
              <i class="bi bi-geo-alt"></i>
            </div>
            <h3 class="info-card__title">Ubicación</h3>
            <p class="info-card__label">Sede oficial:</p>
            <p class="info-card__value">
              {{ eventoInformacion?.ubicacion || "Por definir" }}
            </p>
          </article>

          <!-- CARD 3: Horario -->
          <article class="info-card">
            <div class="info-card__icon">
              <i class="bi bi-clock-history"></i>
            </div>
            <h3 class="info-card__title">Horario</h3>
            <p class="info-card__label">Acceso desde las:</p>
            <p class="info-card__value">
              {{ horaFormateada(eventoInformacion?.hora_inicio) }} –
              {{ horaFormateada(eventoInformacion?.hora_fin) }}
            </p>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.info-wrapper {
  position: relative;
  min-height: 100vh;
  padding: 0;
  overflow: hidden;
}
.hero-bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: brightness(0.75) contrast(1.2);
  z-index: 1;
}
.hero-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(20, 4, 2, 0.75) 50%,
    rgba(0, 0, 0, 0.95) 100%
  );
  z-index: 2;
}
#infoParticles {
  position: absolute;
  inset: 0;
  z-index: 3;
}
.smoke-layer {
  position: absolute;
  inset: 0;
  background: src("/assets/backgrounds/image3.png");
  opacity: 0.80;
  mix-blend-mode: lighten;
  animation: smokeMove 20s infinite linear;
  z-index: 4;
}
@keyframes smokeMove {
  0% {
    transform: translateY(0) scale(1.2);
  }
  100% {
    transform: translateY(-20%) scale(1.25);
  }
}
.info-content {
  position: relative;
  z-index: 20;
  margin-top: calc(80px + 1.5rem);
  padding-bottom: 4rem;
}
.info-section {
  width: min(1200px, 92vw);
  margin: 0 auto;
  padding-top: 1.5rem;
}
.info-title {
  text-align: center;
  color: #fff;
  margin-bottom: 2rem;
}
.info-title h2 {
  font-size: clamp(1.6rem, 2.2vw, 2.2rem);
  font-weight: 700;
  letter-spacing: 0.5px;
  margin-bottom: 0.3rem;
  color: #fff;
}
.info-title p {
  margin: 0;
  opacity: 0.85;
  font-size: clamp(0.95rem, 1.2vw, 1.05rem);
}
.info-grid {
  display: grid;
  gap: 1.4rem;
  grid-template-columns: 1fr;
}
@media (min-width: 768px) {
  .info-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.6rem;
  }
}
.info-card {
  background: rgba(255, 255, 255, 0.09);
  border: 1px solid rgba(0, 170, 255, 0.22);
  border-radius: 18px;
  backdrop-filter: blur(10px);
  padding: 2rem 1.6rem;
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.02) inset, 0 10px 30px rgba(0, 0, 0, 0.25),
    0 0 22px rgba(220, 60, 60, 0.5);
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.info-card:hover {
  transform: translateY(-6px);
  border-color: rgba(204, 0, 0, 0.55);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.04) inset, 0 14px 38px rgba(204, 0, 0, 0.35),
    0 0 30px rgba(204, 0, 0, 0.28);
}
.info-card__icon {
  width: 74px;
  height: 74px;
  border-radius: 50%;
  background: linear-gradient(145deg, #007bff, #00aaff);
  display: grid;
  place-items: center;
  margin-bottom: 1rem;
  box-shadow: 0 0 14px rgba(204, 0, 0, 0.6);
}
.info-card__icon i {
  font-size: 32px;
  color: white;
}
.info-card__title {
  font-size: 1.35rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: #ffffff;
  margin: 0 0 0.6rem 0;
}
.info-card__label {
  margin: 0 0 0.4rem 0;
  font-size: 0.98rem;
  color: rgba(255, 255, 255, 0.78);
}
.info-card__value {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
  color: #ffffff;
}

/* ── Bloques de fecha dentro de la card ── */
.fecha-bloque {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
}
.fecha-divider {
  width: 40px;
  height: 1px;
  background: rgba(204, 0, 0, 0.3);
  margin: 12px 0;
}
.pesaje-hora {
  display: inline;
  font-size: 0.9rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.7);
}

@media (min-width: 768px) and (max-width: 991.98px) {
  .info-content {
    max-width: 100% !important;
    padding-left: 2rem !important;
    padding-right: 2rem !important;
  }
}
@media (max-width: 576px) {
  .info-content {
    margin-top: calc(70px + 1rem);
  }
  .info-card {
    padding: 1.6rem 1.2rem;
    min-height: 230px;
  }
  .info-card__icon {
    width: 64px;
    height: 64px;
  }
  .info-card__icon i {
    font-size: 28px;
  }
}
@media (min-width: 1400px) {
  .info-card {
    min-height: 280px;
  }
}
</style>
